import { useEffect, useMemo, useState } from 'react'
import ReactECharts from 'echarts-for-react'
import { Clock3 } from 'lucide-react'
import type { WireArticle } from '../../api/types'

interface PipelineBottleneckWidgetProps {
  articles: WireArticle[]
  embedded?: boolean
}

const STAGE_LABEL: Record<string, string> = {
  qc_review: 'QC Review',
  layout_review: 'Layout',
  editorial_approval: 'Editorial',
  scheduled: 'Scheduled',
  post_pub_review: 'Post-Pub',
  blocked: 'Blocked',
}

const STAGE_ORDER = ['qc_review', 'layout_review', 'editorial_approval', 'scheduled', 'post_pub_review', 'blocked']

function stageLabel(stage: string): string {
  return STAGE_LABEL[stage] ?? stage.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

function openNeed(article: WireArticle): boolean {
  const stage = article.workflow?.stage
  if (!stage) return false
  if (article.workflow?.activeBlock) return true
  if (stage === 'qc_review') return !article.workflow?.qcAssignedTo
  if (stage === 'layout_review') return !article.workflow?.layoutAssignedTo
  if (stage === 'editorial_approval') return !article.workflow?.editorialAssignedTo
  if (stage === 'post_pub_review') return !article.workflow?.postPubAssignedTo
  return false
}

type BottleneckRow = {
  stage: string
  count: number
}

type ChartColors = {
  fg: string
  line: string
  tooltipBg: string
  tooltipFg: string
}

function resolveChartColors(): ChartColors {
  if (typeof window === 'undefined') {
    return {
      fg: '#111111',
      line: '#e5e7eb',
      tooltipBg: '#111111',
      tooltipFg: '#fcfcfc',
    }
  }
  const s = getComputedStyle(document.documentElement)
  const get = (v: string) => s.getPropertyValue(v).trim()
  return {
    fg: get('--gray-12') || '#111111',
    line: get('--gray-6') || '#e5e7eb',
    tooltipBg: get('--gray-12') || '#111111',
    tooltipFg: get('--gray-1') || '#fcfcfc',
  }
}

function useChartTheme(): ChartColors {
  const [colors, setColors] = useState<ChartColors>(resolveChartColors)
  useEffect(() => {
    const observer = new MutationObserver(() => setColors(resolveChartColors()))
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
    return () => observer.disconnect()
  }, [])
  return colors
}

export function PipelineBottleneckWidget({ articles, embedded = false }: PipelineBottleneckWidgetProps) {
  const colors = useChartTheme()

  const rows = useMemo<BottleneckRow[]>(() => {
    return STAGE_ORDER.map((stage) => {
      const inStage = articles.filter((article) => article.workflow?.stage === stage)

      return {
        stage,
        count: inStage.length,
      }
    }).filter((row) => row.count > 0)
  }, [articles])

  const blockedCount = articles.filter((article) => Boolean(article.workflow?.activeBlock)).length
  const unassignedCount = articles.filter(openNeed).length
  const biggestSlice = rows.slice().sort((a, b) => b.count - a.count)[0]

  const pieOption = useMemo(() => {
    if (!rows.length) return null
    return {
      tooltip: {
        trigger: 'item' as const,
        backgroundColor: colors.tooltipBg,
        borderColor: colors.line,
        textStyle: { color: colors.tooltipFg, fontSize: 11 },
        formatter: (params: { name: string; value: number; percent: number }) => `${params.name}: ${params.value} draft${params.value !== 1 ? 's' : ''} (${params.percent}%)`,
      },
      legend: {
        bottom: 0,
        left: 'center',
        textStyle: { color: colors.fg, fontSize: 11, fontWeight: 600 },
      },
      series: [
        {
          name: 'Draft Distribution',
          type: 'pie' as const,
          radius: ['38%', '68%'],
          center: ['50%', '42%'],
          label: {
            formatter: '{b}: {c}',
            color: colors.fg,
            fontSize: 11,
            fontWeight: 600,
          },
          data: rows.map((row) => ({
            name: stageLabel(row.stage),
            value: row.count,
          })),
        },
      ],
    }
  }, [rows, colors])

  const body = (
    <div className="space-y-2 px-4 py-4">
      {pieOption ? (
        <div className="rounded-xl bg-canvas px-2 py-3">
          <ReactECharts option={pieOption} style={{ height: 260 }} />
        </div>
      ) : (
        <div className="rounded-xl bg-canvas px-4 py-6 text-center text-[11px] text-fg-secondary">
          No draft workflow data available yet.
        </div>
      )}

      {biggestSlice && (
        <div className="rounded-xl bg-fill/60 px-4 py-3 text-[11px] text-fg-secondary">
          Largest slice is <span className="font-semibold text-fg">{stageLabel(biggestSlice.stage)}</span> with {biggestSlice.count} draft{biggestSlice.count !== 1 ? 's' : ''}. {blockedCount} blocked, {unassignedCount} unassigned.
        </div>
      )}

      {!biggestSlice && rows.length === 0 && (
        <div className="rounded-xl bg-fill/60 px-4 py-3 text-[11px] text-fg-secondary">
          {blockedCount} blocked, {unassignedCount} unassigned.
        </div>
      )}
    </div>
  )

  if (embedded) return <div>{body}</div>

  return (
    <section className="flex flex-col rounded border border-line bg-surface">
      <div className="flex items-center gap-2 border-b border-line px-5 py-3">
        <Clock3 size={14} className="text-fg-secondary" />
        <span className="font-sans text-[10px] font-bold uppercase tracking-widest text-fg-secondary">Draft Distribution</span>
      </div>
      {body}
    </section>
  )
}
